import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import {
  BROWSER_TYPE,
  DAMAGE_TYPE,
  DAMAGE_TYPE_COLOR,
  IMAGE_FILETYPE,
  IMAGE_TYPE,
  MARK_DISABLED_COLOR,
  TenantConfig,
  USE_IMAGE_TYPE
} from '../../../app.constants';
import { InspectionImage } from '../../../_objects/inspection-image';
import { InspectionReport } from '../../../_objects/inspection-report';
import { MarkItem } from '../../../_objects/mark';
import { SubjectLocation, SubjectModel } from '../../../_objects/subject-model';
import { TitleItem } from '../../../_objects/title';
import { SubjectModelService } from '../../../_services/http-api/subject-model.service';
import { TenantConfigService } from '../../../_services/http-api/tenant-config.service';
import { UtilsService } from '../../../_services/_utils.service';
import { CommonSnackBarService } from '../../../_services/common-snack-bar.service';
import { TranslateService } from '@ngx-translate/core';
import { LangUpdateService } from '../../../_services/lang-update.service';
import { InspectionImageService } from '../../../_services/http-api/inspection-image.service';
import { SearchSelectOption } from "../../../_shared/component/search-combo-box/search-select-option";
import { BlobService } from '../../../_services/blob.service';
import { IconEnum } from "@evc/web-components";

@Component({
    selector: 'app-image-data-edit',
    templateUrl: './image-data-edit.component.html',
    styleUrls: ['./image-data-edit.component.scss'],
    standalone: false
})
export class ImageDataEditComponent implements OnInit, OnDestroy, OnChanges {
  readonly commentMaxLength = 1000;

  @Input() inspectionImage: InspectionImage;
  @Input() inspectionReport: InspectionReport;
  @Input() marks: MarkItem[];
  @Input() titles: TitleItem[];
  @Input() tags: string[];
  @Input() isEditable: boolean;
  @Input() subjectModelId: string;
  @Input() index: number;
  @Input() listCount: number;
  @Input() isPreviewReport: boolean;
  @Input() locationVersion: number;

  @Output() changeImage = new EventEmitter<number>();
  @Output() closeCompleteSnackBar = new EventEmitter();
  @Output() changeLocationId = new EventEmitter<string>();
  @Output() changeMarkNum = new EventEmitter<number>();
  @Output() changeTitle = new EventEmitter<string>();
  @Output() changeTag = new EventEmitter<string[]>();
  @Output() changeComment = new EventEmitter<string>();
  @Output() toggleUseModified = new EventEmitter<number>();
  @Output() applyMove = new EventEmitter<boolean>();
  @Output() hasError = new EventEmitter<boolean>();

  public titleOptions : SearchSelectOption[];

  imageSrc: string;
  imgSrcBase64: string;
  dspImgWidth: string;
  dspImgHeight: string;
  imgMarginRight: string;
  locations: SubjectLocation[];
  subjectModel: SubjectModel;
  imageListIndex: number;
  isDataPlate = true;
  markControl: FormControl<number> = new FormControl<number>(null);
  titleControl: FormControl<string> = new FormControl<string>('');
  tagsControl: FormControl<string[]> = new FormControl<string[]>([]);
  commentControl: FormControl<string> = new FormControl<string>('');
  locationControl: FormControl<string> = new FormControl<string>('');
  usedImageModeControl: FormControl<number> = new FormControl<number>(null);
  isBackImage = false;
  isForwardImage = false;
  isMoveToOut = false;
  isProcess = false;
  isLoadedImg = false;
  isUpdate = false;
  isShowTags = false;
  rightArrowIcon = IconEnum.AngleRight;
  leftArrowIcon = IconEnum.AngleLeft;
  // ラベル
  markLabel = TenantConfig.DefaultConfigurationValues.LabelOfMark;
  titleLabel = TenantConfig.DefaultConfigurationValues.LabelOfTitle;
  tagsLabel = TenantConfig.DefaultConfigurationValues.LabelOfTags;
  onDestroy$ = new Subject();

  readonly UseImageType = USE_IMAGE_TYPE;
  readonly disabledColorValue = MARK_DISABLED_COLOR;

  constructor(
    private snackBar: CommonSnackBarService,
    private subjectModelService: SubjectModelService,
    private tenantConfigService: TenantConfigService,
    private utilsService: UtilsService,
    private blobService: BlobService,
    private inspectionImageService: InspectionImageService,
    private translate: TranslateService,
    private langUpdateService: LangUpdateService,
  ) {
    this.langUpdateService.prepare(translate, this.onDestroy$);
  }

  async ngOnChanges(changes: SimpleChanges): Promise<void> {
    if ((changes.inspectionImage || changes.marks || changes.title || changes.tags)
      && (this.inspectionImage && this.marks && this.titles && this.tags)) {
      this.isLoadedImg = false;
      this.initForm();
      this.setArrowButton();
      this.loadSubjectLocations(this.subjectModelId, this.locationVersion);
      await this.loadImage();
      this.initTitleOptions(this.titles);
      this.isLoadedImg = true;
    }
  }

  ngOnInit() {
    this.setConfig();
  }

  ngOnDestroy() {
    this.onDestroy$.next(undefined);
    this.onDestroy$.unsubscribe();
  }

  initForm(): void {
    this.locationControl = new FormControl<string>(this.inspectionImage.subjectLocationId);

    this.markControl = this.inspectionImage.metadata
      ? new FormControl<number>(this.inspectionImage.metadata.mark)
      : new FormControl<number>(null);

    this.titleControl = this.inspectionImage.metadata
      ? new FormControl<string>(this.inspectionImage.metadata.title)
      : new FormControl<string>('');

    this.tagsControl = this.inspectionImage.metadata
      ? new FormControl<string[]>(this.inspectionImage.metadata.tags)
      : new FormControl<string[]>([]);

    this.usedImageModeControl = this.inspectionImage.usedImageMode !== undefined
      ? new FormControl<number>(this.inspectionImage.usedImageMode)
      : new FormControl<number>(null);

    this.commentControl = this.inspectionImage.comment
      ? new FormControl<string>(this.inspectionImage.comment, [Validators.maxLength(this.commentMaxLength)])
      : new FormControl<string>('', [Validators.maxLength(this.commentMaxLength)]);

    if (!this.isEditable) {
      this.locationControl.disable();
      this.markControl.disable();
      this.titleControl.disable();
      this.tagsControl.disable();
      this.commentControl.disable();
    }

    if (!this.isEditable || !this.inspectionImage?.hasModifiedImage && !this.inspectionImage?.hasMeasurementImage) {
      this.usedImageModeControl.disable();
    }
  }

  getColor(label: string): string {
    switch (label) {
      case DAMAGE_TYPE.CHIP:
        return DAMAGE_TYPE_COLOR.CHIP;
      case DAMAGE_TYPE.CRACK:
        return DAMAGE_TYPE_COLOR.CRACK;
      case DAMAGE_TYPE.SCRATCH:
        return DAMAGE_TYPE_COLOR.SCRATCH;
      default:
        return '#0000FF';
    }
  }

  isMarkDisabled(color: string): boolean {
    return color === this.disabledColorValue;
  }

  getTooltipText(label: string, score: number): string {
    return this.translate.instant('imageDataEdit.getTooltipText', { label: label, score: (score * 100).toFixed(2) });
  }

  get isVideoContent(): boolean {
    if (!this.inspectionImage?.fileName) {
      return false;
    }
    const fileName = this.inspectionImage.fileName.toLowerCase();
    return fileName.endsWith(IMAGE_FILETYPE.EXTENSION_MP4);
  }

  onTitleChange(selectedValue: string) {
    this.titleControl.setValue(selectedValue);
    this.changeTitleFrom();
  }

  initTitleOptions(titles: TitleItem[]) {
    var defaultTitles :SearchSelectOption[]  = [
      new SearchSelectOption(null, this.translate.instant('multipleImagesEditDialog.noChange')),
      new SearchSelectOption("", `00 - ${this.translate.instant('multipleImagesEditDialog.none')}`)
    ];
    this.titleOptions = defaultTitles.concat(titles
      .map(((title, index) =>
        new SearchSelectOption(title.identifier, `${this.getFormattedTitleIndex(index)} - ${title.identifier}`))));
  }

  getFormattedTitleIndex(index: number) {
    return index < 9 ? `0${index + 1}` : `${index + 1}`;
  }

  loadSubjectLocations(subjectModelId: string, locationVersion: number) {
    this.subjectModelService
      .getSubjectModel(subjectModelId, locationVersion)
      .subscribe(
        data => this.subjectModel = data,
        error => {
          console.log(error);
          this.snackBar.open(this.translate.instant('imageDataEdit.loadSubjectLocationsError'));
        },
        () => {
          this.isDataPlate = this.inspectionImage.subjectLocationId
            ? this.isDataPlateLocation(this.inspectionImage.subjectLocationId)
            : false;

          this.locations = this.utilsService.getLocations(this.subjectModel, null);

          if (this.isDataPlate) {
            this.markControl.disable();
            this.titleControl.disable();
            this.tagsControl.disable();
            this.commentControl.disable();
          }

        });
  }

  getMarkById(identifier: number): MarkItem {
    if (!this.marks) {
      return null;
    }

    // 対応㝙るMark㝌㝪㝋㝣㝟際㝫画僝㝮MarkをX㝨㝗㝦設定
    if (this.marks.find(m => m.identifier === identifier) == undefined) {
      this.markControl.setValue(TenantConfig.DefaultMarkWhenNoCorrespondingMark.DefaultMarkIdentifier);
      return this.marks.find(m => m.identifier === TenantConfig.DefaultMarkWhenNoCorrespondingMark.DefaultMarkIdentifier);
    }

    return this.marks.find(m => m.identifier === identifier);
  }

  getPlaceholder(label: string): string {
    const tmpLabel = label ?? '';
    if (tmpLabel === '') {
      return '';
    } else {
      return this.isEditable
        ? tmpLabel
        : this.translate.instant('imageDataEdit.getPlaceholder', { label: tmpLabel });
    }
  }

  // TenantConfig坖得
  setConfig() {
    const configs = this.tenantConfigService.tenantConfig;
    if (configs) {
      this.markLabel = configs.find(_ => _.key === TenantConfig.LabelOfMark.KEY).value as string;
      this.titleLabel = configs.find(_ => _.key === TenantConfig.LabelOfTitle.KEY).value as string;
      this.tagsLabel = configs.find(_ => _.key === TenantConfig.LabelOfTags.KEY).value as string;
      this.isShowTags = configs.find(_ => _.key === TenantConfig.ShowTagsOnImages.KEY).value as boolean;
    }
  }

  async loadImage(): Promise<void> {
    try {
      const imageSrc = await this.inspectionImageService
        .getImageBinaryUrl(this.inspectionImage.inspectionId, this.inspectionImage.id, USE_IMAGE_TYPE.ORIGINAL);

      const base64 = await this.blobService.getBase64ImageFromUrl(imageSrc);

      // Create a new Image, set the source to the base64 string, and set up the image
      const image = new Image();
      this.imgSrcBase64 = base64;
      image.src = base64;
      image.onload = () => this.setupImage(image);
    } catch (err) {
      // Handle any errors that occur during the image loading process
      this.isLoadedImg = true;
      console.error(err);
    }
  }

  // Image size settings
  setupImage(image: HTMLImageElement): void {
    const mainContent = document.getElementById('data-main-content');
    const dataArea = document.getElementById('data-area');
    const maxImageSize = Math.max(image.width, image.height);
    const minElementSize = Math.min(mainContent.clientWidth - dataArea.clientWidth, mainContent.clientHeight - 158);
    const scale = minElementSize / maxImageSize;

    this.dspImgWidth = (image.width * scale).toString() + 'px';
    this.dspImgHeight = (image.height * scale).toString() + 'px';
    this.imgMarginRight = image.width * scale >= 500 ? '150px' : '50px';
  }

  setArrowButton() {
    this.isBackImage = this.index > 0;
    this.isForwardImage = this.index < this.listCount - 1;
    this.imageListIndex = this.index;
  }

  isDataPlateLocation(subjectLocationId: string): boolean {
    const termLocs = this.utilsService.getLocations(this.subjectModel, null, IMAGE_TYPE.INSPECTIONIMAGE);
    return termLocs.find(loc => loc.identifier === subjectLocationId)?.isDataPlate;
  }

  changeSubjectLocation() {
    if (this.locationControl.invalid) {
      this.hasError.emit(true);
      return;
    }

    const subjectLocationId: string = this.locationControl.value;
    this.isDataPlate = this.isDataPlateLocation(subjectLocationId);

    if (this.isEditable && !this.isDataPlate) {
      this.markControl.enable();
      this.titleControl.enable();
      this.tagsControl.enable();
      this.commentControl.enable();
    } else {
      this.markControl.disable();
      this.titleControl.disable();
      this.tagsControl.disable();
      this.commentControl.disable();
    }

    this.changeLocationId.emit(subjectLocationId);
  }

  changeMarkForm() {
    if (this.markControl.invalid) {
      this.hasError.emit(true);
      return;
    }

    const mark: number = this.markControl.value;
    this.changeMarkNum.emit(mark);
  }

  changeTitleFrom() {
    if (this.titleControl.invalid) {
      this.hasError.emit(true);
      return;
    }

    const title: string = this.titleControl.value;
    this.changeTitle.emit(title);
  }

  changeTagFrom() {
    if (this.tagsControl.invalid) {
      this.hasError.emit(true);
      return;
    }

    const tag: string[] = this.tagsControl.value;
    this.changeTag.emit(tag);
  }

  changeCommentForm() {
    if (this.commentControl.invalid) {
      this.hasError.emit(true);
      return;
    }

    const comment: string = this.commentControl.value;
    this.changeComment.emit(comment);
  }

  changeIndex(isBack: boolean): void {
    this.applyMove.emit(isBack);
  }

  backSwipe() {
    if (this.utilsService.getBrowserName() === BROWSER_TYPE.SAFARI) {
      this.applyMove.emit(true);
    }
  }

  forwardSwipe() {
    if (this.utilsService.getBrowserName() === BROWSER_TYPE.SAFARI) {
      this.applyMove.emit(false);
    }
  }

  checkNoChange(): boolean {
    return this.locationControl.value === this.inspectionImage.subjectLocationId
      && this.markControl.value === this.inspectionImage.metadata.mark
      && (this.titleControl.value === this.inspectionImage.metadata.title || (!this.titleControl.value && !this.inspectionImage.metadata.title))
      && (this.tagsControl.value === this.inspectionImage.metadata.tags || (!this.tagsControl.value && !this.inspectionImage.metadata.tags))
      && (this.commentControl.value === this.inspectionImage.comment || (!this.commentControl.value && !this.inspectionImage.comment));
  }

  toggleUseModifiedImage(): void {
    this.toggleUseModified.emit(this.usedImageModeControl.value);
  }
}
